// 🔧 promotion_post_service.js
// ✅ Purpose: Business service for Promotion Posts
// ✅ Features:
// - Loads Promotion Posts
// - Updates Promotion Posts
// - Deletes Promotion Posts
// - Changes Draft lifecycle state
// - Keeps Draft Manager free of Firestore implementation details
// - Future-ready for approval, scheduling, publishing, and analytics
// - Fully block-commented for clarity and future devs

import {
  deleteDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  getDoc,
  where,
  runTransaction,
} from "firebase/firestore";
import { db } from "../../firebase";
import { getPromotionPostCapabilities, normalizePromotionPostOptions, normalizePromotionHashtags, buildPromotionPostText } from "../../../functions/shared/promotion_post_capabilities.js";
import { canSchedulePromotionPost } from "../helpers/promotion_workflow";
import {
  PROMOTION_PUBLISH_STATUS,
  PROMOTION_QUEUE_STATUS,
} from "../constants/promotion_status";
import {
  getPromotionPostRef,
  getPromotionPostsCollection,
} from "./promotion_repository";
import {
  PROMOTION_SCOPES,
} from "../config/promotion_settings_map";

/**
 * ============================================================================
 * getPromotionPosts
 * ============================================================================
 *
 * Loads all Promotion Posts.
 *
 * Future:
 * - Filtering
 * - Pagination
 * - Search
 * - Platform filters
 * * - Status filters
 */
export const getPromotionPosts = async ({ eventId = null, pageSize = 100 } = {}) => {
  const scopeFilter = eventId
    ? where("eventId", "==", eventId)
    : where("scope", "==", PROMOTION_SCOPES.INTERNAL_LGV);

  const snapshot = await getDocs(
    query(
      getPromotionPostsCollection(),
      scopeFilter,
      orderBy("updatedAt", "desc"),
      limit(pageSize)
    )
  );

  return snapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data(),
  }));
};

// Query the owning scope first so event managers never list another event's posts.
// No queue page-size limit: every promotion derived from this idea is shown.
export const getPromotionPostsForIdea = async ({ ideaId, eventId = null }) => {
  if (!ideaId) return [];
  const scope = eventId ? where("eventId", "==", eventId) : where("scope", "==", PROMOTION_SCOPES.INTERNAL_LGV);
  const snapshot = await getDocs(query(getPromotionPostsCollection(), scope, where("ideaId", "==", ideaId)));
  return snapshot.docs.map((record) => ({ ...record.data(), id: record.id }));
};

/**
 * ============================================================================
 * updatePromotionPost
 * ============================================================================
 *
 * Updates a Promotion Post.
 */
export const updatePromotionPost = async (
  postId,
  updates,
  updatedBy = ""
) => {

  if (!postId) {
    throw new Error("Post ID is required.");
  }

  await runTransaction(db, async (transaction) => {
    const postRef = getPromotionPostRef(postId);
    const snapshot = await transaction.get(postRef);
    if (!snapshot.exists()) throw new Error("Promotion Post not found.");
    const saved = snapshot.data();
    // Saved records represent one immutable variation. Body/media edits stay
    // local to that record; a different identity requires a new variation.
    for (const field of ["platform", "postType", "ideaId", "requestType"]) {
      if (Object.prototype.hasOwnProperty.call(updates, field) &&
          (saved[field] ?? (field === "requestType" ? "social_post" : "")) !== updates[field]) {
        throw new Error("Platform, Post Type, Request Type and Idea are locked after saving. Create another promotion for a different variation.");
      }
    }
    const changes = { ...updates };
    if (Object.prototype.hasOwnProperty.call(changes, "aiPrompt")) {
      if (typeof changes.aiPrompt !== "string" || !changes.aiPrompt.trim()) throw new Error("AI Prompt / Idea is required.");
      changes.aiPrompt = changes.aiPrompt.trim();
      if (saved.aiPrompt?.trim() && saved.aiPrompt.trim() !== changes.aiPrompt) {
        throw new Error("AI Prompt / Idea is locked after saving. Create another Idea to change its context.");
      }
      // Legacy saved prompts may contain padding. Retain their exact bytes so
      // a body-only edit satisfies immutable-field Rules without a migration.
      if (saved.aiPrompt?.trim()) changes.aiPrompt = saved.aiPrompt;
    }
    if ((saved.requestType || "social_post") === "social_post" &&
        ["postOptions", "hashtags", "cta", "body"].some((key) => Object.prototype.hasOwnProperty.call(changes, key))) {
      const effective = { ...saved, ...changes };
      const capabilities = getPromotionPostCapabilities(effective.platform, effective.postType);
      changes.postOptions = normalizePromotionPostOptions(effective.platform, effective.postType, effective.postOptions || {}, {
        imageCount: (effective.rawImages || effective.imageUrls || []).length,
        videoDurationSeconds: effective.rawVideo?.durationSeconds,
      });
      changes.hashtags = normalizePromotionHashtags(effective.platform, effective.postType, effective.hashtags || []);
      changes.cta = capabilities.supportsCaption ? String(effective.cta || "").trim() : "";
      if (capabilities.supportsCaption) buildPromotionPostText({ ...effective, hashtags: changes.hashtags, cta: changes.cta });
    }
    transaction.update(postRef, { ...changes, updatedBy, updatedAt: serverTimestamp() });
  });

  return true;
};

/**
 * ============================================================================
 * deletePromotionPost
 * ============================================================================
 *
 * Deletes a Promotion Post.
 *
 * MVP:
 * Permanent delete.
 *
 * Future:
 * Soft delete / archive.
 */
export const deletePromotionPost = async (
  postId
) => {

  if (!postId) {
    throw new Error("Post ID is required.");
  }

  await deleteDoc(
    getPromotionPostRef(postId)
  );

  return true;
};

/**
 * ============================================================================
 * getPromotionPost
 * ============================================================================
 *
 * Loads a single Promotion Post.
 *
 * Used by Promotion Studio when editing an
 * existing draft.
 */
export const getPromotionPost = async (
  postId
) => {

  if (!postId) {
    throw new Error(
      "Post ID is required."
    );
  }

  const snapshot =
    await getDoc(
      getPromotionPostRef(postId)
    );

  if (!snapshot.exists()) {
    throw new Error(
      "Promotion Post not found."
    );
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };

};

/**
 * ============================================================================
 * savePromotionPostChanges
 * ============================================================================
 *
 * Saves edits made to an existing Promotion Post.
 *
 * Future:
 * - Audit history
 * - Version history
 * - Change tracking
 */
export const savePromotionPostChanges =
async (
  postId,
  updates,
  updatedBy = ""
) => {

  if (!postId) {
    throw new Error(
      "Post ID is required."
    );
  }

  return updatePromotionPost(postId, updates, updatedBy);

};

/**
 * ============================================================================
 * approvePromotionPost
 * ============================================================================
 *
 * Moves a Promotion Post from Draft status
 * into Approved status.
 *
 * Approved posts are ready for scheduling.
 */
export const approvePromotionPost = async (
  postId,
  updatedBy = ""
) => {

  if (!postId) {
    throw new Error(
      "Post ID is required."
    );
  }

  await updateDoc(
    getPromotionPostRef(postId),
    {

      queueStatus:
        PROMOTION_QUEUE_STATUS.APPROVED,

      updatedBy,

      approvedBy:
        updatedBy,

      approvedAt:
        serverTimestamp(),

      updatedAt:
        serverTimestamp(),

    }
  );

  return true;

};

/**
 * ============================================================================
 * schedulePromotionPost
 * ============================================================================
 *
 * Moves an Approved Promotion Post into
 * Scheduled publishing status.
 *
 * Scheduled posts are ready for the future
 * Publishing Engine to process.
 */
export const schedulePromotionPost = async ({
  postId,
  scheduledAt,
  updatedBy = "",
}) => {

  if (!postId) {
    throw new Error(
      "Post ID is required."
    );
  }

  if (!scheduledAt) {
    throw new Error(
      "Scheduled date/time is required."
    );
  }

  const date = new Date(scheduledAt);
  if (Number.isNaN(date.getTime()) || date <= new Date()) throw new Error("Select a future posting date and time.");
  const postRef = getPromotionPostRef(postId);
  // Re-read this record transactionally so stale forms cannot reset a post
  // already claimed/published by the scheduler or modify another platform row.
  await runTransaction(db, async (transaction) => {
    const snapshot = await transaction.get(postRef);
    const post = snapshot.exists() ? snapshot.data() : null;
    if (!canSchedulePromotionPost(post) || post.queueStatus !== PROMOTION_QUEUE_STATUS.APPROVED) {
      throw new Error("Only approved, unpublished promotion records can be scheduled.");
    }
    transaction.update(postRef, {
      queueStatus: PROMOTION_QUEUE_STATUS.APPROVED,
      publishStatus: PROMOTION_PUBLISH_STATUS.SCHEDULED,
      scheduledAt: date.toISOString(),
      updatedBy,
      scheduledBy: updatedBy,
      scheduledAtSetAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  });

  return true;

};
